/**
 * Shop The Look
 * Handles multi-product quick shop drawer for outfit items
 */

class ShopTheLook {
  constructor(drawerElement) {
    this.drawer = drawerElement;
    this.outfitId = drawerElement.dataset.outfitId;
    this.productForms = new Map();
    
    this.#init();
  }

  #init() {
    this.#setupVariantSelectors();
    this.#setupAddToCartHandlers();
    this.#setupAddAllHandler();
  }

  #setupVariantSelectors() {
    const variantInputs = this.drawer.querySelectorAll('.variant-selector-input');
    
    for (const input of variantInputs) {
      input.addEventListener('change', (e) => {
        this.#handleVariantChange(e.target);
      });
    }
  }

  #handleVariantChange(input) {
    const productContainer = input.closest('[data-product-index]');
    if (!productContainer) return;

    const productIndex = productContainer.dataset.productIndex;
    const productId = productContainer.dataset.productId;
    
    // Get all selected options for this product
    const selectedOptions = this.#getSelectedOptions(productContainer);
    
    // Find matching variant
    const variantsData = this.#getProductVariants(productIndex);
    const matchingVariant = this.#findVariantByOptions(variantsData, selectedOptions);
    
    if (matchingVariant) {
      this.#updateProductUI(productContainer, matchingVariant, productIndex);
    }
  }

  #getSelectedOptions(container) {
    const selectedInputs = container.querySelectorAll('.variant-selector-input:checked');
    const options = [];
    
    for (const input of selectedInputs) {
      options.push(input.value);
    }
    
    return options;
  }

  #getProductVariants(productIndex) {
    const variantsScript = this.drawer.querySelector(`[data-product-variants="${productIndex}"]`);
    if (!variantsScript) return [];
    
    try {
      return JSON.parse(variantsScript.textContent);
    } catch (e) {
      console.error('Error parsing product variants:', e);
      return [];
    }
  }

  #getProductVariantPrices(productIndex) {
    const pricesScript = this.drawer.querySelector(`[data-product-variant-prices="${productIndex}"]`);
    if (!pricesScript) return {};

    try {
      return JSON.parse(pricesScript.textContent);
    } catch (e) {
      console.error('Error parsing variant prices:', e);
      return {};
    }
  }

  #findVariantByOptions(variants, selectedOptions) {
    return variants.find(variant => {
      if (!variant.options || variant.options.length !== selectedOptions.length) {
        return false;
      }
      
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });
  }

  #updateProductUI(container, variant, productIndex) {
    const variantInput = container.querySelector(`[data-variant-input="${productIndex}"]`);
    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.disabled = !variant.available;
    }

    const priceElement = container.querySelector(`[data-product-price="${productIndex}"]`);
    if (priceElement) {
      const formattedPrice = this.#getProductVariantPrices(productIndex)[variant.id];
      if (formattedPrice) {
        /*
         * The Liquid `money` filter outputs an HTML fragment
         * (e.g. `<span class="money">£139.00</span>`). Set as innerHTML so it
         * renders the same way the initial server render does — using
         * textContent would surface the raw markup.
         */
        priceElement.innerHTML = formattedPrice;
      }
    }

    const addToCartButton = container.querySelector(`[data-stl-add-button="${productIndex}"]`);
    if (addToCartButton) {
      const buttonText = addToCartButton.querySelector('.button-text');
      const availableText = addToCartButton.dataset.textAvailable || 'Add to basket';
      const unavailableText = addToCartButton.dataset.textUnavailable || 'Sold out';

      if (variant.available) {
        addToCartButton.disabled = false;
        if (buttonText) buttonText.textContent = availableText;
      } else {
        addToCartButton.disabled = true;
        if (buttonText) buttonText.textContent = unavailableText;
      }
    }

    /*
     * Variant-selector adds a loading class to the dropdown summary on click
     * because PDP performs an async section re-fetch. The drawer updates UI
     * synchronously, so clear the loading state immediately.
     */
    const loadingTriggers = container.querySelectorAll('.variant-dropdown__summary--loading');
    for (const trigger of loadingTriggers) {
      trigger.classList.remove('variant-dropdown__summary--loading');
    }
  }

  #setupAddToCartHandlers() {
    const forms = this.drawer.querySelectorAll('[data-type="add-to-cart-form"]');
    
    for (const form of forms) {
      form.addEventListener('submit', (e) => {
        this.#handleAddToCart(e);
      });
    }
  }

  #setupAddAllHandler() {
    const addAllButton = this.drawer.querySelector('[data-add-all-to-cart]');
    
    if (addAllButton) {
      addAllButton.addEventListener('click', () => {
        this.#handleAddAllToCart();
      });
    }
  }

  async #handleAddToCart(event) {
    event.preventDefault();

    const form = event.target;
    const productIndex = form.dataset.productIndex;
    const button = form.querySelector(`[data-stl-add-button="${productIndex}"]`);
    const buttonText = button?.querySelector('.button-text');
    const spinner = button?.querySelector('.loading-overlay__spinner');

    if (!button) return;

    button.disabled = true;
    if (buttonText) buttonText.classList.add('hidden');
    if (spinner) spinner.classList.remove('hidden');

    const restoreButton = () => {
      if (buttonText) {
        buttonText.textContent = button.dataset.textAvailable || 'Add to basket';
        buttonText.classList.remove('hidden');
      }
      if (spinner) spinner.classList.add('hidden');
      button.disabled = false;
    };

    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.description || result.message || 'Failed to add item to cart');
      }

      /*
       * CartDrawer listens for `cart:add` and runs refreshSections() + opens
       * the cart drawer. The cart drawer sits later in the DOM than the
       * shop-the-look drawer, so it naturally renders over it at the same
       * z-index. Keep the shop-the-look drawer open so the buyer can keep
       * adding more outfit pieces.
       */
      document.dispatchEvent(new CustomEvent('cart:add', {
        detail: { item: result, source: 'shop-the-look' }
      }));

      restoreButton();
    } catch (error) {
      console.error('Error adding to cart:', error);

      if (buttonText) {
        buttonText.textContent = 'Error - Try again';
        buttonText.classList.remove('hidden');
      }
      if (spinner) spinner.classList.add('hidden');

      setTimeout(restoreButton, 3000);
    }
  }

  async #handleAddAllToCart() {
    const addAllButton = this.drawer.querySelector('[data-add-all-to-cart]');
    const buttonText = addAllButton?.querySelector('.button-text');
    const spinner = addAllButton?.querySelector('.loading-overlay__spinner');
    const messageEl = this.drawer.querySelector('[data-add-all-message]');

    if (!addAllButton) return;

    // Collect all variant IDs from available products
    // Use both data attributes to target only product containers (not forms which also have data-product-index)
    const items = [];
    const productContainers = this.drawer.querySelectorAll('.shop-the-look-product[data-product-index]');

    for (const container of productContainers) {
      const variantInput = container.querySelector('input[name="id"]');
      const addButton = container.querySelector('button[type="submit"]');

      // Only add if variant input exists, has a value, and is available
      if (variantInput && variantInput.value && !variantInput.disabled && addButton && !addButton.disabled) {
        items.push({
          id: parseInt(variantInput.value, 10),
          quantity: 1
        });
      }
    }

    if (items.length === 0) {
      if (messageEl) {
        messageEl.textContent = 'No available products to add';
        setTimeout(() => messageEl.textContent = '', 3000);
      }
      return;
    }

    // Show loading state
    addAllButton.disabled = true;
    if (buttonText) buttonText.classList.add('hidden');
    if (spinner) spinner.classList.remove('hidden');

    try {
      // Add all items in a single request using Shopify's items array format
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add items to cart');
      }

      const result = await response.json();
      const addedCount = result.items?.length || items.length;

      if (spinner) spinner.classList.add('hidden');

      // Close shop-the-look drawer
      if (window.Drawer) {
        window.Drawer.close(this.outfitId);
      }

      // Refresh cart and open cart drawer
      if (window.cartDrawer) {
        await window.cartDrawer.refreshSections();
        if (window.Drawer) {
          window.Drawer.open('cart-drawer');
        }
      } else {
        // Fallback: dispatch cart:add event which CartDrawer listens to
        document.dispatchEvent(new CustomEvent('cart:add', {
          detail: {
            source: 'shop-the-look',
            count: addedCount
          }
        }));
      }

      // Reset button state
      if (buttonText) {
        buttonText.textContent = 'Add All to Basket';
        buttonText.classList.remove('hidden');
      }
      addAllButton.disabled = false;
      if (messageEl) messageEl.textContent = '';

    } catch (error) {
      console.error('Error adding all to cart:', error);

      if (spinner) spinner.classList.add('hidden');

      // Show error state
      if (buttonText) {
        buttonText.textContent = 'Error - Try again';
        buttonText.classList.remove('hidden');
      }
      if (messageEl) {
        messageEl.textContent = error.message || 'Could not add items';
      }

      setTimeout(() => {
        if (buttonText) buttonText.textContent = 'Add All to Basket';
        addAllButton.disabled = false;
        if (messageEl) messageEl.textContent = '';
      }, 3000);
    }
  }

  // Static method to initialize all shop-the-look drawers
  static initAll() {
    const drawers = document.querySelectorAll('[data-shop-the-look]');
    
    for (const drawer of drawers) {
      if (!drawer.__shopTheLook) {
        drawer.__shopTheLook = new ShopTheLook(drawer);
      }
    }
  }
}

/**
 * Scripts inside cloned template content do not run automatically; replace
 * them so inline size-guide bootstraps execute once per hydration.
 */
const activateInlineScripts = (container) => {
  const scripts = Array.from(container.querySelectorAll('script'));

  for (const oldScript of scripts) {
    const type = (oldScript.getAttribute('type') || 'text/javascript').trim().toLowerCase();

    if (type === 'application/json' || type === 'application/ld+json') {
      continue;
    }

    if (type === 'module') {
      const s = document.createElement('script');
      s.type = 'module';
      s.textContent = oldScript.textContent;
      oldScript.replaceWith(s);
      continue;
    }

    if (oldScript.src) {
      const s = document.createElement('script');
      s.src = oldScript.src;
      if (oldScript.async) s.async = true;
      if (oldScript.defer) s.defer = true;
      oldScript.replaceWith(s);
      continue;
    }

    const s = document.createElement('script');
    s.textContent = oldScript.textContent;
    oldScript.replaceWith(s);
  }
};

const hydrateShopTheLookDrawer = (drawerId) => {
  if (!drawerId || !drawerId.startsWith('shop-look-')) {
    return;
  }

  const drawerRoot = document.querySelector(`[data-drawer-root="${CSS.escape(drawerId)}"]`);
  if (!drawerRoot || drawerRoot.dataset.stlHydrated === 'true') {
    return;
  }

  const templateId = `ShopTheLook-${drawerId}`;
  const tmpl = document.getElementById(templateId);
  const drawerContent = drawerRoot.querySelector('[data-drawer-content]');

  if (!tmpl || !drawerContent) {
    return;
  }

  drawerContent.replaceChildren();
  drawerContent.appendChild(tmpl.content.cloneNode(true));
  drawerRoot.dataset.stlHydrated = 'true';

  activateInlineScripts(drawerContent);

  if (typeof window.jeanstoreRefreshMetaobjectSizeGuides === 'function') {
    window.jeanstoreRefreshMetaobjectSizeGuides(drawerContent);
  }

  if (typeof window.jeanstoreRefreshLegacySizeGuides === 'function') {
    window.jeanstoreRefreshLegacySizeGuides(drawerContent);
  }

  ShopTheLook.initAll();
};

document.addEventListener(
  'click',
  (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;

    const id = opener.getAttribute('data-drawer-open');
    if (!id) return;

    hydrateShopTheLookDrawer(id);
  },
  true
);

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ShopTheLook.initAll());
} else {
  ShopTheLook.initAll();
}

// Re-initialize when new content is added (e.g., via theme editor)
document.addEventListener('shopify:section:load', () => ShopTheLook.initAll());

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.ShopTheLook = ShopTheLook;
  window.jeanstoreHydrateShopTheLookDrawer = hydrateShopTheLookDrawer;
}

